import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { fetchCreatorDirectory } from "@/lib/library-hubs.functions";
import { breadcrumbJsonLd, datasetJsonLd } from "@/components/library-hub";
import { SiteFooter } from "@/components/site-footer";

const TITLE = "Creators in the Vibing Library";
const DESC =
  "The makers behind the library — every creator with more than one tool, demo or resource featured in the Vibing Library, ranked by how much of their work we feature.";

export const Route = createFileRoute("/library/creators")({
  loader: async () => {
    try {
      return { creators: await fetchCreatorDirectory() };
    } catch {
      return { creators: [] };
    }
  },
  head: () => {
    const url = "https://vibing.inc/library/creators";
    return {
      meta: [
        { title: `${TITLE} | The Vibing Library` },
        { name: "description", content: DESC.slice(0, 155) },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESC.slice(0, 155) },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        ...ogImageMeta("library", "Share card: creators in the Vibing Library"),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: datasetJsonLd({
            name: TITLE,
            description: DESC,
            url,
            count: 0,
            keywords: ["creators", "makers", "curated tools"],
          }),
        },
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", item: "https://vibing.inc/" },
            { name: "The Vibing Library", item: "https://vibing.inc/library" },
            { name: "Creators", item: url },
          ]),
        },
      ],
    };
  },
  component: Creators,
});

function Creators() {
  const { creators } = Route.useLoaderData();
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li className="flex items-center gap-2">
              <Link to="/" className="transition-colors hover:text-foreground">
                Home
              </Link>
              <span aria-hidden>/</span>
            </li>
            <li className="flex items-center gap-2">
              <Link to="/library" className="transition-colors hover:text-foreground">
                The Vibing Library
              </Link>
              <span aria-hidden>/</span>
            </li>
            <li className="text-foreground">Creators</li>
          </ol>
        </nav>

        <p className="mt-8 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          The Vibing Library — creators
        </p>
        <h1
          className="mt-4 font-display tracking-[-0.035em] leading-[1.04] max-w-[22ch]"
          style={{ fontSize: "clamp(2rem, 4.6vw, 3.6rem)" }}
        >
          The makers behind the library
        </h1>
        <p className="mt-6 max-w-[62ch] text-base md:text-lg leading-relaxed text-muted-foreground text-pretty">
          {DESC}
        </p>

        {creators.length === 0 ? (
          <p className="mt-12 text-muted-foreground">
            Creator profiles are warming up — check back in a moment.
          </p>
        ) : (
          <ul className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {creators.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/library/by/$creator"
                  params={{ creator: c.slug }}
                  className="flex min-h-[76px] items-center justify-between gap-4 rounded-2xl border border-border px-5 py-4 transition-colors hover:bg-muted"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{c.name}</span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {c.handle ? `@${c.handle}` : c.categories[0] || "Featured creator"}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-muted px-3 py-1 text-sm tabular-nums text-muted-foreground">
                    {c.count}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
